"use client";

import * as React from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  SortingState,
} from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StudyNotUploadedDocument, StudyDocument } from "../../types";
import { formatFileSize } from "../../hooks/useUserLimits";
import { cn } from "@/lib/utils";

/* -------------------------
   Props
------------------------- */

interface CourseSourcesTableProps {
  sources: StudyDocument[];
  files: StudyNotUploadedDocument[];
  selected: StudyNotUploadedDocument[];
  setSelected: React.Dispatch<React.SetStateAction<StudyNotUploadedDocument[]>>;

  allowedMimeTypes: string[];
  maxFileSizeBytes: number;
}

/* -------------------------
   Validation
------------------------- */

type FileValidationResult = {
  valid: boolean;
  reason?: "size" | "type";
};

function parseFileSizeToBytes(input?: string): number | null {
  if (!input) return null;

  const match = input.trim().match(/^([\d.]+)\s*(B|KB|MB|GB|TB)?$/i);
  if (!match) return null;

  const value = parseFloat(match[1]);
  const unit = (match[2] || "B").toUpperCase();

  const multipliers: Record<string, number> = {
    B: 1,
    KB: 1024,
    MB: 1024 ** 2,
    GB: 1024 ** 3,
    TB: 1024 ** 4,
  };

  return Math.round(value * (multipliers[unit] ?? 1));
}

function validateDocument(
  doc: StudyNotUploadedDocument,
  opts: {
    allowedMimeTypes: string[];
    maxFileSizeBytes: number;
  }
): FileValidationResult {
  const mime = doc.metadata?.content_type;

  if (
    mime &&
    opts.allowedMimeTypes.length > 0 &&
    !opts.allowedMimeTypes.includes(mime)
  ) {
    return { valid: false, reason: "type" };
  }

  const size =
    typeof doc.metadata?.size === "number"
      ? doc.metadata?.size
      : "size_text" in doc.metadata
      ? parseFileSizeToBytes(doc.metadata.size_text)
      : 0;

  if (size != null && size > opts.maxFileSizeBytes) {
    return { valid: false, reason: "size" };
  }

  return { valid: true };
}

/* -------------------------
   Component
------------------------- */

export function CourseSourcesTable({
  sources,
  files,
  selected,
  setSelected,
  allowedMimeTypes,
  maxFileSizeBytes,
}: CourseSourcesTableProps) {
  const isLocked = (doc: StudyNotUploadedDocument) =>
    !!sources.find(
      (f) =>
        "canvas_file_id" in f.metadata &&
        f.metadata.canvas_file_id == doc.metadata.canvas_file_id
    );

  const [sorting, setSorting] = React.useState<SortingState>([
    // { desc: true, id: "created" },
  ]);
  const [globalFilter, setGlobalFilter] = React.useState("");

  const getValidation = (doc: StudyNotUploadedDocument) =>
    validateDocument(doc, {
      allowedMimeTypes,
      maxFileSizeBytes,
    });

  const isInvalid = (doc: StudyNotUploadedDocument) =>
    !getValidation(doc).valid;

  const isSelected = (doc: StudyNotUploadedDocument) =>
    !isInvalid(doc) && selected.some((s) => s == doc);

  const toggleRow = (doc: StudyNotUploadedDocument) => {
    if (isInvalid(doc) || isLocked(doc)) return;

    setSelected((prev) =>
      isSelected(doc) ? prev.filter((s) => s != doc) : [...prev, doc]
    );
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelected(files.filter((s) => !isInvalid(s)));
    } else {
      setSelected([]);
    }
  };

  const columns = React.useMemo<ColumnDef<StudyNotUploadedDocument>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => {
          const rows = table.getRowModel().rows.map((r) => r.original);
          const selectableRows = rows.filter((r) => !isInvalid(r));
          const allSelected =
            selectableRows.length > 0 &&
            selectableRows.every((r) => isSelected(r));

          return (
            <Checkbox
              checked={allSelected}
              onCheckedChange={(v) => toggleAll(!!v)}
              aria-label="Select all"
            />
          );
        },
        cell: ({ row }) => {
          const doc = row.original;
          const invalid = isInvalid(doc);
          const locked = isLocked(doc);

          return (
            <Checkbox
              checked={isSelected(doc) || locked}
              disabled={invalid || locked}
              aria-label={`Select ${doc.name}`}
            />
          );
        },
        enableSorting: false,
      },
      {
        id: "name",
        accessorKey: "name",
        enableSorting: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row, getValue }) => {
          const validation = getValidation(row.original);

          return (
            <div
              className={cn(
                "max-w-full truncate",
                validation.reason === "type" && "text-red-500 font-medium"
              )}
            >
              {getValue<string>()}
              {validation.reason === "type" && " (unsupported type)"}
            </div>
          );
        },
      },
      {
        id: "size",
        accessorKey: "metadata.size",
        header: "Size",
        cell: ({ row }) => {
          const doc = row.original;
          const size =
            "size_text" in row.original.metadata
              ? row.original.metadata.size_text
              : row.original.metadata.size
              ? formatFileSize(row.original.metadata.size)
              : "—";
          const validation = getValidation(doc);

          return (
            <span
              className={cn(
                validation.reason === "size" && "text-red-500 font-medium"
              )}
            >
              {size}
              {validation.reason === "size" && " (too large)"}
            </span>
          );
        },
      },
      {
        id: "created",
        accessorKey: "metadata.created_at",
        enableSorting: true,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="px-0"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Created
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="capitalize">
            {row.original.metadata.created_at
              ? new Date(row.original.metadata.created_at).toLocaleString()
              : "-"}
          </span>
        ),
      },
    ],
    [selected, allowedMimeTypes, maxFileSizeBytes]
  );

  const table = useReactTable({
    data: files.sort((a, b) => {
      const aInvalid = isInvalid(a) || isLocked(a);
      const bInvalid = isInvalid(b) || isLocked(b);

      if (aInvalid && !bInvalid) return 1;
      if (!aInvalid && bInvalid) return -1;
      if (
        new Date(a.metadata.created_at).valueOf() >
        new Date(b.metadata.created_at).valueOf()
      )
        return -1;
      return 0;
    }),
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="w-full">
        <Input
          placeholder="Search sources..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          variant="search"
        />
      </div>

      <div className="rounded-md border border-theme bg-theme-0 max-h-[350px] overflow-y-scroll">
        <Table className="table-fixed w-full">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      header.column.id === "name" && "w-[40%]",
                      header.column.id === "select" && "w-10",
                      header.column.id === "size" && "w-32",
                      header.column.id === "created" && "w-48",
                      "bg-theme-2"
                    )}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={isSelected(row.original) ? "selected" : undefined}
                  onClick={() => toggleRow(row.original)}
                  className={cn(
                    "cursor-pointer hover:bg-theme-1",
                    isSelected(row.original) && "bg-theme-1",
                    (isInvalid(row.original) || isLocked(row.original)) &&
                      "opacity-50 pointer-events-none"
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        cell.column.id === "name" && "w-[40%]",
                        cell.column.id === "select" && "w-10",
                        cell.column.id === "size" && "w-32",
                        cell.column.id === "created" && "w-48"
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-theme-1"
                >
                  No sources found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
